import React, { useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { BusinessTravellerBudgetInfoModal } from './BusinessTravellerBudgetInfoModal'
import ModalContext from 'contexts/ModalContext'
import Clickable from 'components/Common/Clickable/Clickable'
import moment from 'moment'
import Expander from 'components/utils/Expander'
import PageBanner from 'LuxKit/Banners/PageBanner'
import useBusinessTravellerBusinessBudgetFetcher from 'businessTraveller/hooks/useBusinessTravellerBusinessBudgetFetcher'
import useFetchItemPolicyBudget from 'businessTraveller/hooks/useFetchItemPolicyBudget'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import BusinessTravellerPolicyDebugButton from 'businessTraveller/components/policy-debug/BusinessTravellerPolicyDebugButton'
import { useItemPolicyShadowFetcher } from 'businessTraveller/hooks/itemPolicy/useItemPolicyShadowFetcher'
import { generateItemPolicyHotelBudgetKey } from 'businessTraveller/utils/generateItemPolicyCalculationKey'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectSelectedTravellerEmployeeIds } from 'businessTraveller/selectors/itemPolicy/selectSelectedTravellerEmployeeIds'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
`

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  roomCount?: number;
  className?: string;
  offer?: App.AnyOffer;
}

function BusinessTravellerStickyBudgetBar(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    roomCount = 1,
    className,
    offer,
  } = props
  const numberOfNights = useMemo(() => {
    if (!checkInDate || !checkOutDate) {
      return undefined
    }
    return moment(checkOutDate).diff(checkInDate, 'days') || 1
  }, [checkInDate, checkOutDate])

  const { currentCurrency: currencyCode } = useContext(GeoContext)
  const employeeIds = useAppSelector(selectSelectedTravellerEmployeeIds)
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)
  const itemPolicyBudgets = useAppSelector(state => state.businessTraveller.itemPolicyBudgets)

  const { budgetData, budgetKey, toBeCalculatedBusinessBudget } = useBusinessTravellerBusinessBudgetFetcher(offer)

  const itemPolicyCacheKey = useMemo(() => {
    if (!toBeCalculatedBusinessBudget || numberOfNights == null) {
      return ''
    }
    return generateItemPolicyHotelBudgetKey({
      currencyCode,
      longitude: toBeCalculatedBusinessBudget.longitude,
      latitude: toBeCalculatedBusinessBudget.latitude,
      lePlaceId: toBeCalculatedBusinessBudget.lePlaceId,
      numberOfNights,
      roomCount,
      employeeIds,
    })
  }, [currencyCode, employeeIds, numberOfNights, roomCount, toBeCalculatedBusinessBudget])

  const hasValidItemPolicyLocation = !!(
    toBeCalculatedBusinessBudget?.lePlaceId ||
    (toBeCalculatedBusinessBudget?.latitude && toBeCalculatedBusinessBudget?.longitude)
  )

  useFetchItemPolicyBudget({
    enabled: hasValidItemPolicyLocation && numberOfNights != null,
    businessId,
    cacheKey: itemPolicyCacheKey,
    itemPolicyType: 'HOTEL',
    currencyCode,
    numberOfNights: numberOfNights ?? 0,
    roomCount,
    employeeIds,
    lePlaceId: toBeCalculatedBusinessBudget?.lePlaceId,
    latitude: toBeCalculatedBusinessBudget?.latitude,
    longitude: toBeCalculatedBusinessBudget?.longitude,
  })

  const itemPolicyData = itemPolicyCacheKey
    ? itemPolicyBudgets?.[itemPolicyCacheKey]
    : undefined

  useItemPolicyShadowFetcher({
    enabled: true,
    businessId,
    cacheKey: itemPolicyCacheKey,
    item: undefined,
    employeeIds,
    vertical: 'hotel',
    surface: 'budget',
    legacyData: budgetData,
    itemPolicyData,
  })

  const moreThanOneBudget = budgetData?.budgets ? budgetData.budgets?.length > 1 : false
  const maxBudget = (budgetData?.maxNightlyBudget ?? 0) * (numberOfNights ?? 0)
  const minBudget = (budgetData?.minNightlyBudget ?? 0) * (numberOfNights ?? 0)
  const showMinBudget = maxBudget !== minBudget
  const isBudgetBarShown = !!maxBudget

  const showModal = useContext(ModalContext)
  const toggleModal = useCallback(() => {
    if (budgetData?.budgets) {
      showModal(<BusinessTravellerBudgetInfoModal
        budgets={budgetData.budgets}
      />)
    }
  }, [budgetData?.budgets, showModal])

  const budgetBarRef = useRef(null)
  useHeaderStateObserver('businessTravellerBudgetBar', budgetBarRef, isBudgetBarShown)

  if (!isBudgetBarShown) {
    return null
  }

  return <StickyContainer className={className} ref={budgetBarRef}>
    <Expander expanded={budgetData?.status === 'success'}>
      <PageBanner
        kind="promotional-primary"
        align="center"
        description={<b>
          Accommodation budget:{' '}
          {!!showMinBudget && <><FormatCurrency value={minBudget} format="dynamicDollar" /> to{' '}</>}
          <FormatCurrency value={maxBudget} format="dynamicDollar" /> per room {roomCount > 1 && <>(<FormatCurrency value={roomCount * maxBudget} format="dynamicDollar" /> total)</>}
        </b>}
        trailingContent={
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {moreThanOneBudget && (
              <Clickable onClick={toggleModal}>
                <LineInfoCircleIcon size="S" />
              </Clickable>
            )}
            <BusinessTravellerPolicyDebugButton
              source="legacy"
              vertical="hotel"
              surface="budget"
              primaryData={budgetData}
              comparisonData={itemPolicyData}
              cacheKey={budgetKey}
              employeeIds={employeeIds}
            />
          </Group>
        }
      />
    </Expander>
  </StickyContainer>
}

export default BusinessTravellerStickyBudgetBar
